import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { withStyles } from 'theme/withStyles';
import getKeyboardFocusableElements from 'utils/getKeyboardFocusableElements';
import SessionTimeout from 'dialogs/SessionTimeout';
import Default from 'dialogs/Default';
import SelectAddress from 'dialogs/SelectAddress';
import MobileValidation from 'dialogs/MobileValidation';
import HomeDocumentUpload from 'dialogs/HomeDocumentUpload';
import HomeAgreements from 'dialogs/HomeAgreements';
import HomeDisclosures from 'dialogs/HomeDisclosures';
import HomeDocuments from 'dialogs/HomeDocuments';
import Esign from 'dialogs/Esign';
import EsignViewOnly from 'dialogs/EsignViewOnly';
import HomeVerifyDeposits from 'dialogs/HomeVerifyDeposits';
import Confirm from 'dialogs/Confirm';
import Rates from 'dialogs/Rates';
import VerifyUser from 'dialogs/VerifyUser';
import Warning from 'dialogs/Warning';
import Remove from 'dialogs/Remove';

const styles = {
  backdrop: {
    background: '#000',
    height: '100%',
    left: 0,
    opacity: 0.6,
    position: 'fixed',
    top: 0,
    width: '100%',
  },
  modal: {
    '&.hide-overflow': {
      overflow: 'hidden',
    },
    alignItems: 'center',
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    left: 0,
    maxHeight: '100%',
    overflowY: 'auto',
    position: 'fixed',
    right: 0,
    top: 0,
  },
  root: {
    position: 'relative',
    // Dialogs should always be on top
    zIndex: 9999,
  },
};

const getWrapClass = () => {
  const regexAndroid = /Android/i;
  const regexIOS = /iPhone|iPad|iPod/i;
  const regexWindowsPhone = /Windows Phone/i;

  if (navigator.userAgent.match(regexAndroid)) {
    return 'isAndroid';
  }
  if (navigator.userAgent.match(regexIOS)) {
    return 'isIOS';
  }
  if (navigator.userAgent.match(regexWindowsPhone)) {
    return 'isWindowsPhone';
  }
  return 'isDesktop';
};

const Dialog = ({ classes, onClose, type }) => {
  const commonClasses = `modal-dialog glu-dialog dialog-custom ${getWrapClass()}`;
  const dialogContent = useMemo(() => {
    /** istanbul ignore switch */
    switch (type) {
      case 'sessiontimeout':
        return <SessionTimeout commonClasses={commonClasses} />;
      case 'selectaddress':
        return <SelectAddress commonClasses={commonClasses} />;
      case 'mobilevalidation_userauth':
      case 'mobilevalidation':
        return <MobileValidation commonClasses={commonClasses} />;
      case 'home_document_upload':
        return <HomeDocumentUpload commonClasses={commonClasses} />;
      case 'home_agreements':
        return <HomeAgreements commonClasses={commonClasses} />;
      case 'home_disclosures':
        return <HomeDisclosures commonClasses={commonClasses} />;
      case 'home_verify_deposits':
        return <HomeVerifyDeposits commonClasses={commonClasses} />;
      case 'home_documents':
        return <HomeDocuments commonClasses={commonClasses} />;
      case 'home_esign':
        return <Esign commonClasses={commonClasses} isHomeEsign />;
      case 'esign':
        return <Esign commonClasses={commonClasses} />;
      case 'esignViewOnly':
        return <EsignViewOnly commonClasses={commonClasses} />;
      case 'confirm':
        return <Confirm commonClasses={commonClasses} />;
      case 'rates':
        return <Rates commonClasses={commonClasses} />;
      case 'verifyuser':
        return <VerifyUser commonClasses={commonClasses} />;
      case 'warning':
        return <Warning commonClasses={commonClasses} />;
      case 'remove':
        return <Remove commonClasses={commonClasses} />;
      case 'disclosure':
      default:
        return <Default commonClasses={commonClasses} />;
    }
  }, [commonClasses, type]);

  useEffect(() => {
    document.body.classList.add('modal-open');
    const [focusEl] = [
      ...document.querySelectorAll(
        '.modal-header h4, .modal-body p, .modal-footer button[variant="primary"]',
      ),
    ].filter((el) => el.innerText !== '');
    if (focusEl) {
      focusEl.setAttribute('tabIndex', -1);
      focusEl.focus();
    }

    // Make background content inaccessible while dialog is open
    const focusableElements = getKeyboardFocusableElements(document.querySelector('#main'));
    focusableElements.forEach((el) => el.setAttribute('tabindex', -1));

    return () => {
      if (typeof onClose === 'function') {
        onClose();
      }
      document.body.classList.remove('modal-open');
      focusableElements.forEach((el) => el.removeAttribute('tabindex'));
    };
  }, [onClose]);
  return (
    <div
      role="dialog"
      tabIndex="-1"
      aria-labelledby="modal-title"
      aria-describedby="modal-body"
      className={classnames('glu-modal', classes.root)}
    >
      <div
        className={classnames('modal-backdrop in', classes.backdrop)}
        style={{ display: 'block' }}
      />
      <div className={classnames('modal', classes.modal, { 'hide-overflow': type === 'esign' })}>
        {dialogContent}
      </div>
    </div>
  );
};

Dialog.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  onClose: PropTypes.func,
  type: PropTypes.string,
};

Dialog.defaultProps = {
  onClose: () => null,
  type: null,
};

export default withStyles(styles)(Dialog);
